import { Resources } from 'constants/resources';

import React from 'react';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Form, useCreate, useNotify } from 'react-admin';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { TypographyWithBold } from 'shared/mui/Typography';
import { Create } from 'shared/react-admin/Create/Create';

import {
  MerchantsShowSettingsForm,
  MerchantsShowSettingsFormValues,
} from '../components/MerchantsShowSettingsForm';

type Props = {
  open: boolean;
  onClose: () => void;
  merchantId: string | undefined;
  providerId: string | undefined;
  providerName: string;
};

export const MerchantsShowSettingsCreateDialog = ({
  open,
  onClose,
  merchantId,
  providerId,
  providerName,
}: Props) => {
  const translate = useTypedTranslate();

  const [create] = useCreate();
  const notify = useNotify();

  const onSubmit = (formValues: MerchantsShowSettingsFormValues) => {
    create(
      Resources.MerchantProviderSettings,
      {
        data: {
          merchant: formValues.merchant,
          provider: formValues.provider,
          merchantAccount: formValues.merchantAccount,
          percent: formValues.percent ?? 0,
          enabled: true,
        },
      },
      {
        onSuccess: () => {
          notify(
            <TypographyWithBold
              text={translate('app.merchants.providerSettingsSuccess', { name: providerName })}
            />,
            { type: 'success' },
          );
          onClose();
        },
        onError: (error: any) => {
          notify(`${translate('app.common.error')}: ${(error as Error).message}`, {
            type: 'error',
          });
        },
      },
    );
  };

  return (
    <Dialog onClose={onClose} open={open} scroll="body" width="640px">
      <Create
        resource={Resources.MerchantProviderSettings}
        sx={{ '.RaCreate-noActions': { marginTop: 0 } }}
      >
        <Form
          defaultValues={{
            merchant: merchantId,
            provider: providerId,
          }}
          onSubmit={onSubmit as any}
        >
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title={translate('app.merchants.providerSettingsCreate')}
            titleSize="smallBold"
          />
          <CardContent>
            <MerchantsShowSettingsForm merchantId={merchantId} providerId={providerId} />
          </CardContent>
          <CardActionsButtons
            rightActionsSlot={[
              <Button
                key="cancel"
                label={translate('app.buttons.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              <Button
                key="create"
                label={translate('app.buttons.add')}
                type="submit"
                variant="contained"
              />,
            ]}
          />
        </Form>
      </Create>
    </Dialog>
  );
};
